import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';

import {makeSelectBasicUser} from 'reducers/selectors/userSelectors';
import request from 'utils/request';
import './style.scss';

const PROMPT_ENDPOINT = 'translations/admin/prompt';
const basicUserSelector = makeSelectBasicUser();

const emptyPrompt = {
  businessRules: '',
  revision: 0,
  version: '',
  source: '',
  updatedAt: '',
  updatedBy: '',
  limits: {
    minLength: null,
    maxLength: null,
    domainMinLength: null,
    domainMaxLength: null,
  },
  languageRules: [],
  domainRules: {},
  domains: [],
  scope: {},
};

const asLimit = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

const unwrapPrompt = (response) => {
  if (response && response.data && typeof response.data === 'object') {
    return response.data;
  }
  return response;
};

export const normalizePrompt = (response) => {
  const payload = unwrapPrompt(response);
  if (!payload || typeof payload !== 'object') return null;
  const domains = Array.isArray(payload.domains)
    ? payload.domains.filter(
      (domain) => domain && typeof domain === 'object' && typeof domain.key === 'string'
    )
    : [];
  const domainRules = domains.reduce((rules, domain) => ({
    ...rules,
    [domain.key]: typeof payload.domainRules?.[domain.key] === 'string'
      ? payload.domainRules[domain.key]
      : '',
  }), {});

  return {
    ...emptyPrompt,
    ...payload,
    businessRules:
      typeof payload.businessRules === 'string' ? payload.businessRules : '',
    revision: payload.revision == null ? 0 : payload.revision,
    limits: {
      minLength: asLimit(payload.limits?.minLength),
      maxLength: asLimit(payload.limits?.maxLength),
      domainMinLength: asLimit(payload.limits?.domainMinLength),
      domainMaxLength: asLimit(payload.limits?.domainMaxLength),
    },
    languageRules: Array.isArray(payload.languageRules)
      ? payload.languageRules
      : [],
    domainRules,
    domains,
    scope: payload.scope && typeof payload.scope === 'object' ? payload.scope : {},
  };
};

const normalizedRuleLength = (value) => (typeof value === 'string' ? value : '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.replace(/[\t ]+$/g, ''))
    .join('\n')
    .trim().length;

export const validatePromptRule = (
  value,
  {minLength, maxLength, required = false, label = 'Rule'} = {}
) => {
  const normalizedLength = normalizedRuleLength(value);
  const normalizedMinLength = asLimit(minLength);
  const normalizedMaxLength = asLimit(maxLength);

  if (!normalizedLength) return required ? `${label} are required.` : '';
  if (normalizedMinLength !== null && normalizedLength < normalizedMinLength) {
    return `${label} must be at least ${normalizedMinLength} characters.`;
  }
  if (normalizedMaxLength !== null && normalizedLength > normalizedMaxLength) {
    return `${label} must be no more than ${normalizedMaxLength} characters.`;
  }
  return '';
};

export const validateBusinessRules = (value, limits = {}) => validatePromptRule(value, {
  minLength: limits?.minLength,
  maxLength: limits?.maxLength,
  required: true,
  label: 'Business rules',
});

const errorText = (requestError) => {
  if (!requestError) return '';
  if (typeof requestError === 'string') return requestError;
  return requestError.message || requestError.error || requestError.code || '';
};

export const isRevisionConflict = (requestError) => {
  const message = `${errorText(requestError)} ${requestError?.status || ''}`;
  return /PROMPT_REVISION_CONFLICT|changed/i.test(message) || Number(requestError?.status) === 409;
};

const formatRequestError = (requestError, action) => {
  const detail = errorText(requestError);
  if (action === 'save' && isRevisionConflict(requestError)) {
    return 'The translation prompt changed while you were editing. Reload before saving so no changes are overwritten.';
  }
  if (action === 'save') {
    return `The translation prompt could not be saved${detail ? `: ${detail}` : '.'} Reload to try again.`;
  }
  return `The translation prompt could not be loaded${detail ? `: ${detail}` : '.'} Reload to try again.`;
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') {
    return (
      value.label ||
      value.name ||
      value.email ||
      value.username ||
      value._id ||
      '—'
    );
  }
  return String(value);
};

const LanguageRules = ({languageRules}) => (
  <section className="translation-prompt__section" aria-labelledby="translation-prompt-language-rules">
    <div className="translation-prompt__section-heading">
      <div>
        <h2 id="translation-prompt-language-rules">Language rules</h2>
        <p>
          The server appends these immutable target-language rules after the
          business rules for every Prompt-driven AI request.
        </p>
      </div>
      <span>Read-only</span>
    </div>
    {languageRules.length ? (
      <div className="translation-prompt__table-wrap">
        <table className="translation-prompt__table">
          <thead>
            <tr>
              <th scope="col">Code</th>
              <th scope="col">English name</th>
              <th scope="col">Native name</th>
              <th scope="col">Instruction</th>
            </tr>
          </thead>
          <tbody>
            {languageRules.map((rule, index) => (
              <tr key={`${rule.code || 'language'}-${index}`}>
                <td>{displayValue(rule.code)}</td>
                <td>{displayValue(rule.englishName)}</td>
                <td>{displayValue(rule.nativeName)}</td>
                <td>{displayValue(rule.instruction)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <p className="translation-prompt__empty">No language rules are configured.</p>
    )}
  </section>
);

const TranslationPrompt = () => {
  const basicUser = useSelector(basicUserSelector);
  const authToken = basicUser?.authToken;
  const [prompt, setPrompt] = useState(null);
  const [businessRules, setBusinessRules] = useState('');
  const [domainRules, setDomainRules] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const mountedRef = useRef(false);
  const requestVersionRef = useRef(0);

  const loadPrompt = useCallback(async () => {
    if (!authToken) return;
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    setLoading(true);
    setError('');

    try {
      const response = await request(PROMPT_ENDPOINT, authToken);
      const nextPrompt = normalizePrompt(response);
      if (!nextPrompt) throw new Error('The server returned an invalid prompt.');
      if (mountedRef.current && requestVersion === requestVersionRef.current) {
        setPrompt(nextPrompt);
        setBusinessRules(nextPrompt.businessRules);
        setDomainRules(nextPrompt.domainRules);
      }
    } catch (requestError) {
      if (mountedRef.current && requestVersion === requestVersionRef.current) {
        setError(formatRequestError(requestError, 'load'));
      }
    } finally {
      if (mountedRef.current && requestVersion === requestVersionRef.current) {
        setLoading(false);
      }
    }
  }, [authToken]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestVersionRef.current += 1;
    };
  }, []);

  useEffect(() => {
    loadPrompt();
  }, [loadPrompt]);

  const validationError = useMemo(
    () => validateBusinessRules(businessRules, prompt?.limits),
    [businessRules, prompt?.limits]
  );
  const domainValidationErrors = useMemo(() => (
    (prompt?.domains || []).reduce((errors, domain) => ({
      ...errors,
      [domain.key]: validatePromptRule(domainRules[domain.key], {
        minLength: prompt?.limits?.domainMinLength,
        maxLength: prompt?.limits?.domainMaxLength,
        label: `${domain.label || domain.key} rules`,
      }),
    }), {})
  ), [domainRules, prompt?.domains, prompt?.limits]);
  const hasDomainValidationError = Object.values(domainValidationErrors).some(Boolean);
  const hasChanges = Boolean(prompt && (
    businessRules !== prompt.businessRules ||
    prompt.domains.some((domain) => (
      (domainRules[domain.key] || '') !== (prompt.domainRules[domain.key] || '')
    ))
  ));
  const canSave = Boolean(
    prompt &&
      hasChanges &&
      !loading &&
      !saving &&
      !validationError &&
      !hasDomainValidationError &&
      authToken
  );

  const handleBusinessRulesChange = (event) => {
    setBusinessRules(event.target.value);
    if (error) setError('');
  };

  const handleDomainRuleChange = (domainKey, value) => {
    setDomainRules((currentRules) => ({...currentRules, [domainKey]: value}));
    if (error) setError('');
  };

  const savePrompt = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const body = {
        businessRules,
        expectedRevision: prompt.revision,
      };
      if (prompt.domains.length) {
        body.domainRules = prompt.domains.reduce((rules, domain) => ({
          ...rules,
          [domain.key]: domainRules[domain.key] || '',
        }), {});
      }
      const response = await request(PROMPT_ENDPOINT, authToken, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      const nextPrompt = normalizePrompt(response);
      if (!nextPrompt) throw new Error('The server returned an invalid prompt.');
      if (mountedRef.current) {
        setPrompt(nextPrompt);
        setBusinessRules(nextPrompt.businessRules);
        setDomainRules(nextPrompt.domainRules);
      }
    } catch (requestError) {
      if (mountedRef.current) setError(formatRequestError(requestError, 'save'));
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const limits = prompt?.limits || emptyPrompt.limits;

  return (
    <section className="translation-prompt" aria-labelledby="translation-prompt-title">
      <div className="translation-prompt__heading">
        <div>
          <h1 id="translation-prompt-title">AI Translation Prompt</h1>
          <p className="translation-prompt__scope-note" data-testid="translation-prompt-google-warning">
            All Prompt-driven AI providers share these business rules. Google NMT does
            not support custom prompts, and its naming rules are not guaranteed.
          </p>
        </div>
        <div className="translation-prompt__actions">
          <button
            type="button"
            className="translation-prompt__reload"
            onClick={loadPrompt}
            disabled={loading || saving || !authToken}
          >
            Reload
          </button>
          <button
            type="button"
            className="translation-prompt__save"
            onClick={savePrompt}
            disabled={!canSave}
            data-testid="translation-prompt-save"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {error && (
        <div className="translation-prompt__message translation-prompt__message--error" role="alert">
          <span>{error}</span>
          <button type="button" onClick={loadPrompt} disabled={loading || saving || !authToken}>
            Reload
          </button>
        </div>
      )}
      {loading && !prompt && (
        <p className="translation-prompt__message" role="status">Loading translation prompt…</p>
      )}

      {prompt && (
        <>
          <div className="translation-prompt__metadata" aria-label="Prompt metadata">
            <div><span>Version</span><strong>{displayValue(prompt.version)}</strong></div>
            <div><span>Revision</span><strong>{displayValue(prompt.revision)}</strong></div>
            <div><span>Updated at</span><strong>{displayValue(prompt.updatedAt)}</strong></div>
            <div><span>Updated by</span><strong>{displayValue(prompt.updatedBy)}</strong></div>
            {prompt.source && <div><span>Source</span><strong>{displayValue(prompt.source)}</strong></div>}
          </div>

          <section className="translation-prompt__section" aria-labelledby="translation-prompt-business-rules">
            <div className="translation-prompt__section-heading">
              <div>
                <h2 id="translation-prompt-business-rules">Global business rules</h2>
                <p>
                  JSON output, safety, and target-language constraints are
                  server managed and cannot be edited.{' '}
                  {limits.minLength !== null ? `Minimum ${limits.minLength} characters. ` : ''}
                  {limits.maxLength !== null ? `Maximum ${limits.maxLength} characters.` : ''}
                </p>
              </div>
              <span>{businessRules.length} characters</span>
            </div>
            <label className="translation-prompt__label" htmlFor="translation-prompt-business-rules-input">
              Editable Prompt business rules
            </label>
            <textarea
              id="translation-prompt-business-rules-input"
              data-testid="translation-prompt-business-rules"
              value={businessRules}
              onChange={handleBusinessRulesChange}
              disabled={loading || saving}
              aria-invalid={Boolean(validationError)}
              rows={12}
            />
            {validationError && (
              <p className="translation-prompt__field-error" role="alert">{validationError}</p>
            )}
          </section>

          {prompt.domains.length > 0 && (
            <section className="translation-prompt__section" aria-labelledby="translation-prompt-domain-rules">
              <div className="translation-prompt__section-heading">
                <div>
                  <h2 id="translation-prompt-domain-rules">Page Folder supplements</h2>
                  <p>
                    These optional rules are added after the global rules for
                    their Page Folder. Leave a field blank to use only the
                    global rules.{' '}
                    {limits.domainMinLength !== null
                      ? `Non-empty rules require at least ${limits.domainMinLength} characters. `
                      : ''}
                    {limits.domainMaxLength !== null
                      ? `Maximum ${limits.domainMaxLength} characters.`
                      : ''}
                  </p>
                </div>
              </div>
              <div className="translation-prompt__domain-rules">
                {prompt.domains.map((domain) => {
                  const value = domainRules[domain.key] || '';
                  const fieldError = domainValidationErrors[domain.key];
                  const inputId = `translation-prompt-domain-${domain.key}-input`;
                  return (
                    <div className="translation-prompt__domain-rule" key={domain.key}>
                      <div className="translation-prompt__domain-rule-heading">
                        <label className="translation-prompt__label" htmlFor={inputId}>
                          {domain.label || domain.key}
                        </label>
                        <span>{value.length} characters</span>
                      </div>
                      {domain.description && <p>{domain.description}</p>}
                      <textarea
                        id={inputId}
                        data-testid={`translation-prompt-domain-${domain.key}`}
                        value={value}
                        onChange={(event) => handleDomainRuleChange(domain.key, event.target.value)}
                        disabled={loading || saving}
                        aria-invalid={Boolean(fieldError)}
                        rows={6}
                      />
                      {fieldError && (
                        <p className="translation-prompt__field-error" role="alert">{fieldError}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <LanguageRules languageRules={prompt.languageRules} />
        </>
      )}
    </section>
  );
};

export default TranslationPrompt;
